import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, distinctUntilChanged, filter, map, Observable, take } from 'rxjs';
import { selectAuth } from 'src/app/store/auth/auth.reducers';
import { UserSettingsService } from './user-settings.service';
import { CELL_RIGHT_CLICK_KEY, CellRightClickPref, coerceCellRightClick } from './cell-context-menu.util';

const CHANNEL_NAME = 'user-settings';

/**
 * App-wide cache of the user's right-click-on-cell preference. `CellCopyDirective` exists
 * once per rendered cell and reads this inside an event handler, so the value must be
 * readable synchronously and must never trigger an HTTP call per cell. Loaded once per
 * authenticated transition (mirrors DateFormatService) and mutated optimistically.
 * Shares the 'user-settings' BroadcastChannel with DateFormatService; each service
 * ignores keys it does not own.
 */
@Injectable({ providedIn: 'root' })
export class CellContextMenuService implements OnDestroy {
  private readonly pref = new BehaviorSubject<CellRightClickPref>('menu');
  private initialized = false;
  private channel?: BroadcastChannel;

  readonly pref$: Observable<CellRightClickPref> = this.pref.asObservable();

  /** Current preference, read synchronously by the directive's contextmenu handler. */
  get current(): CellRightClickPref {
    return this.pref.value;
  }

  constructor(private userSettings: UserSettingsService, private store: Store) {
    if (typeof BroadcastChannel !== 'undefined') {
      this.channel = new BroadcastChannel(CHANNEL_NAME);
      this.channel.onmessage = (e: MessageEvent) => this.applyBroadcast(e.data);
    }
  }

  ngOnDestroy(): void {
    this.channel?.close();
  }

  /** Wire the per-login load. Safe to call more than once. Invoked from AppComponent. */
  init(): void {
    if (this.initialized) return;
    this.initialized = true;
    this.store
      .select(selectAuth)
      .pipe(
        map(authState => authState.isAuthenticated),
        distinctUntilChanged(),
        filter(isAuth => isAuth),
      )
      .subscribe(() => this.load());
  }

  setPref(value: CellRightClickPref): void {
    this.pref.next(value);
    // The default value means "no stored override" -> DELETE; otherwise upsert.
    const op = value === 'menu'
      ? this.userSettings.reset(CELL_RIGHT_CLICK_KEY)
      : this.userSettings.set(CELL_RIGHT_CLICK_KEY, value);
    op.pipe(take(1)).subscribe({ error: () => {} });
    this.channel?.postMessage({ key: CELL_RIGHT_CLICK_KEY, value });
  }

  private load(): void {
    this.userSettings
      .getAll()
      .pipe(take(1))
      .subscribe(settings => this.pref.next(coerceCellRightClick(settings[CELL_RIGHT_CLICK_KEY])));
  }

  private applyBroadcast(data: { key?: string; value?: unknown }): void {
    if (data?.key !== CELL_RIGHT_CLICK_KEY) return;
    this.pref.next(coerceCellRightClick(data.value));
  }
}
